import { useField } from "formik";
import {
  Box,
  Text,
  TextArea,
  TextField,
  Button,
  IconButton,
  Flex,
} from "@radix-ui/themes";
import { cn } from "@/utils/cn";
import { useState, useEffect } from "react";
import { Check, Pencil, X, Eye, EyeOff } from "lucide-react";

interface InputFieldProps {
  name: string;
  label?: string;
  type?:
    | "text"
    | "number"
    | "date"
    | "datetime-local"
    | "email"
    | "password"
    | "search"
    | "tel"
    | "time"
    | "url"
    | "week";
  step?: string | number;
  min?: string | number;
  max?: string | number;
  required?: boolean;
  as?: "textarea";
  className?: string;
  readonly?: boolean;
  disabled?: boolean;
  description?: string;
  options?: {
    addon_before?: string | null;
    addon_after?: string | null;
    placeholder?: string | null;
    masked?: boolean;
    rows?: number;
  } | null;
  isEditing?: boolean;
  individualEditAllowed?: boolean;
  onChange?: (e: string) => void;
  onEditSaved?: (name: string, value: string) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export default function InputField({
  name,
  label,
  type = "text",
  step,
  min,
  max,
  required = false,
  as,
  className,
  disabled,
  readonly,
  description,
  options,
  isEditing = true,
  individualEditAllowed = false,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: InputFieldProps) {
  const [field, { error, touched }, helpers] = useField(name);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>("");
  const [isMaskedVisible, setIsMaskedVisible] = useState(false);

  useEffect(() => {
    if (field.value !== undefined) {
      setTempValue(field.value);
    }
  }, [field.value]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsIndividualEditing(true);
    setTempValue(field.value);
    onIndividualEditModeChanged?.(true);
  };

  const handleSave = () => {
    helpers.setValue(tempValue);
    onChange?.(tempValue);
    onEditSaved?.(name, tempValue);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleCancel = () => {
    setTempValue(field.value);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleChange = (value: string) => {
    setTempValue(value);
  };

  // Handle date input formatting
  const formatDateForInput = (
    value: string | Date | null | undefined,
  ): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return "";

    if (type === "date") {
      return date.toISOString().split("T")[0];
    } else if (type === "datetime-local") {
      // Format for datetime-local input (YYYY-MM-DDThh:mm)
      return date.toISOString().slice(0, 16);
    }
    return value.toString();
  };

  // Handle date input changes
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      if (isIndividualEditing) {
        handleChange(value);
      } else {
        field.onChange(e);
        onChange?.(value);
      }
      return;
    }

    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      // Convert to ISO string for consistent API format
      const isoString = date.toISOString();
      if (isIndividualEditing) {
        handleChange(isoString);
      } else {
        field.onChange({
          ...e,
          target: { ...e.target, name: name, value: isoString },
        });
        onChange?.(isoString);
      }
    } else {
      if (isIndividualEditing) {
        handleChange(value);
      } else {
        field.onChange(e);
        onChange?.(value);
      }
    }
  };

  // Determine current field state
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;
  const isReadonlyMode = !isEditing && !isIndividualEditing;

  // Determine the actual input type to use
  const getInputType = () => {
    if (options?.masked && !isMaskedVisible) {
      return "password";
    }
    if (options?.masked && isMaskedVisible) {
      return "text";
    }
    return type;
  };

  // Toggle masked field visibility
  const toggleMaskedVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMaskedVisible(!isMaskedVisible);
  };

  return (
    <div className="mb-4 w-[100%]">
      <Box mb="2">
        {label && (
          <div className="flex items-center justify-between">
            <Text as="label" htmlFor={name} size="2" weight="medium" mb="1">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
          </div>
        )}

        {description && (
          <Text size="1" color="gray" mb="1">
            {description}
          </Text>
        )}
      </Box>

      {as === "textarea" ? (
        <TextArea
          className={cn(
            "w-full",
            isReadonlyMode && "readonly-input",
            className,
          )}
          id={name}
          placeholder={options?.placeholder || undefined}
          readOnly={readonly || isReadonlyMode}
          disabled={disabled}
          rows={options?.rows || 3}
          onClick={
            isReadonlyMode && individualEditAllowed ? handleEdit : undefined
          }
          style={{
            cursor:
              isReadonlyMode && individualEditAllowed ? "pointer" : undefined,
          }}
          value={isIndividualEditing ? tempValue : field.value}
          onChange={(e) => {
            if (isIndividualEditing) {
              handleChange(e.target.value);
            } else {
              field.onChange(e);
              onChange?.(e.target.value);
            }
          }}
        />
      ) : (
        <TextField.Root
          className={cn(
            "w-full",
            isReadonlyMode && "readonly-input",
            className,
          )}
          id={name}
          type={getInputType()}
          step={type === "number" ? step : undefined}
          min={type === "number" ? min : undefined}
          max={type === "number" ? max : undefined}
          placeholder={options?.placeholder || undefined}
          readOnly={readonly || isReadonlyMode}
          disabled={disabled}
          onClick={
            isReadonlyMode && individualEditAllowed ? handleEdit : undefined
          }
          style={{
            cursor:
              isReadonlyMode && individualEditAllowed ? "pointer" : undefined,
          }}
          value={
            type === "date" || type === "datetime-local"
              ? formatDateForInput(
                  isIndividualEditing ? tempValue : field.value,
                )
              : isIndividualEditing
                ? tempValue
                : field.value
          }
          onChange={
            type === "date" || type === "datetime-local"
              ? handleDateChange
              : (e) => {
                  if (isIndividualEditing) {
                    handleChange(e.target.value);
                  } else {
                    field.onChange(e);
                    onChange?.(e.target.value);
                  }
                }
          }
        >
          {options?.addon_before && (
            <TextField.Slot side="left">{options.addon_before}</TextField.Slot>
          )}

          {(options?.masked || options?.addon_after || shouldShowEditIcon) && (
            <TextField.Slot side="right">
              <div className="flex items-center gap-1">
                {options?.masked && (
                  <IconButton
                    size="1"
                    type="button"
                    variant="ghost"
                    onClick={toggleMaskedVisibility}
                    aria-label={isMaskedVisible ? "Hide value" : "Show value"}
                  >
                    {isMaskedVisible ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </IconButton>
                )}
                {options?.addon_after && !options?.masked && (
                  <span>{options.addon_after}</span>
                )}
                {shouldShowEditIcon && (
                  <IconButton
                    size="1"
                    type="button"
                    variant="ghost"
                    onClick={handleEdit}
                    aria-label="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </IconButton>
                )}
              </div>
            </TextField.Slot>
          )}
        </TextField.Root>
      )}

      {isIndividualEditing && !isEditing && (
        <Flex gap="2" mt="2" justify="start">
          <Button size="1" variant="soft" color="red" onClick={handleCancel}>
            <X width={12} height={12} style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button size="1" variant="soft" color="green" onClick={handleSave}>
            <Check width={12} height={12} style={{ marginRight: 4 }} />
            Save
          </Button>
        </Flex>
      )}

      {error && touched && (
        <Text color="red" size="1" mt="1">
          {error}
        </Text>
      )}
    </div>
  );
}
